import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

export const useUnreadCount = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        // Get user's listings across all 5 asset types for inquiries
        const [heliRes, engineRes, partsRes, jobRes, serviceRes] = await Promise.all([
          supabase.from('helicopter_listings').select('id').eq('user_id', user.id),
          supabase.from('engine_listings').select('id').eq('user_id', user.id),
          supabase.from('parts_listings').select('id').eq('user_id', user.id),
          supabase.from('job_listings').select('id').eq('user_id', user.id),
          supabase.from('service_listings').select('id').eq('user_id', user.id),
        ]);

        const firstError = [heliRes, engineRes, partsRes, jobRes, serviceRes].find(r => r.error);
        if (firstError?.error) throw firstError.error;

        const listingIds = [
          ...(heliRes.data || []),
          ...(engineRes.data || []),
          ...(partsRes.data || []),
          ...(jobRes.data || []),
          ...(serviceRes.data || []),
        ].map(l => l.id);

        // Count unread inquiries
        const { count: inquiryCount, error: inquiryError } = await supabase
          .from('listing_inquiries')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'new')
          .in('listing_id', listingIds);

        if (inquiryError) throw inquiryError;

        // Count unread messages - RLS handles filtering to user's conversations
        const { count: messageCount, error: messageError } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('is_read', false)
          .neq('sender_id', user.id);

        if (messageError) throw messageError;

        // Total unread count is inquiries + messages
        setUnreadCount((inquiryCount || 0) + (messageCount || 0));
      } catch (error) {
        console.error('Error fetching unread count:', error);
        setUnreadCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchUnreadCount();

    // Listen for immediate mark-as-read events
    const handleMessagesRead = () => {
      fetchUnreadCount();
    };
    window.addEventListener('messages:read', handleMessagesRead);

    // Set up real-time subscriptions with debouncing
    let debounceTimer: NodeJS.Timeout;
    const debouncedFetch = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchUnreadCount(), 200);
    };

    const messageChannel = supabase
      .channel('unified-unread-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        debouncedFetch
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        },
        debouncedFetch
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'listing_inquiries'
        },
        debouncedFetch
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('messages:read', handleMessagesRead);
      supabase.removeChannel(messageChannel);
    };
  }, [user?.id]);

  return { unreadCount, loading };
};